import { defineColumns } from './piColumns.js';
import { EMPTY, formatDateFull, formatInteger, formatPercent, formatPeriod, isNumber } from './piFormat.js';
import {
  JOURNEY_OPTIONS, MAX_PAGE_SIZE, SIZE_BAND_OPTIONS, buildApiQuery, journeyLabel, journeyText, nextSort, optionLabel, profileText, toQueryString,
} from './piQuery.js';
import { companyLink } from './piNavigation.js';
import { listOf, objectOf } from './piRequests.js';
import { countText, metricCell, ratioText, shareOf } from './piTable.js';
import { stateDescription, stateLabel, stateTone } from './piState.js';

export const COMPANY_ADOPTION_FIELDS = ['period', 'roleClass', 'sizeBand', 'segment'];

export const MIN_ELIGIBLE_OPTIONS = [5, 10, 20, 50];

export const DEFAULT_MIN_ELIGIBLE = 10;

export const MAX_MIN_ELIGIBLE = 10000;

export const COMPANY_ADOPTION_SORTS = ['adoption', 'adopters', 'eligible'];

export const COMPANY_ADOPTION_PAGE_SIZE = 25;

export const COMPANY_ADOPTION_QUERY = { minEligible: 'minEligible', sort: 'companiesSort', desc: 'companiesDesc', journey: 'companiesJourney' };

export const COMPANY_ADOPTION_QUERY_KEYS = Object.keys(COMPANY_ADOPTION_QUERY).map((key) => COMPANY_ADOPTION_QUERY[key]);

export const DEFAULT_COMPANY_ADOPTION_LIST = {
  minEligible: DEFAULT_MIN_ELIGIBLE, journey: '', sort: 'adoption', desc: true, page: 1, pageSize: COMPANY_ADOPTION_PAGE_SIZE, base: '',
};

const recurrenceShareOf = (row) => {
  const source = objectOf(row);
  return isNumber(source.recurrenceUsers) && isNumber(source.adoptingUsers) && source.adoptingUsers > 0
    ? shareOf(source.recurrenceUsers, source.adoptingUsers)
    : null;
};

export const COMPANY_ADOPTION_COLUMNS = defineColumns([
  { key: 'rank', label: '#', type: 'int', infoKey: 'features.byCompany.rank', description: 'Posição na ordem escolhida, contando as páginas anteriores.' },
  {
    key: 'company', label: 'Empresa', type: 'text', infoKey: 'common.company', width: 40,
    value: (row) => textOf(objectOf(row).name) || textOf(objectOf(row).companyId),
  },
  { key: 'companyId', label: 'Identificador da empresa', type: 'text', infoKey: 'common.companyId', screen: false },
  { key: 'shardCode', label: 'Banco', type: 'text', infoKey: 'common.shard', screen: false },
  {
    key: 'sizeBand', label: 'Porte', type: 'text', infoKey: 'common.sizeBand', screen: false,
    value: (row) => (textOf(objectOf(row).sizeBand) ? optionLabel(SIZE_BAND_OPTIONS, textOf(objectOf(row).sizeBand)) : ''),
  },
  { key: 'segment', label: 'Perfil da empresa', type: 'text', infoKey: 'common.segment', screen: false, value: (row) => textOf(objectOf(row).segment) },
  {
    key: 'journey', label: 'Jornada no fim da janela', type: 'text', infoKey: 'features.journeyAtEnd', screen: false,
    value: (row) => journeyLabel(objectOf(row).clientJourney === undefined ? objectOf(row).journey : objectOf(row).clientJourney),
  },
  {
    key: 'adoption', label: 'Adoção', type: 'metric', unit: 'pct', infoKey: 'features.byCompany.adoption', sortable: true, startDescending: true,
    description: 'Adotantes ÷ elegíveis da empresa nos 30 dias.',
  },
  {
    key: 'adopters', label: 'Adotantes', type: 'int', infoKey: 'features.byCompany.adopters', sortable: true, startDescending: true,
    description: 'Elegíveis da empresa que usaram a funcionalidade nos 30 dias.', value: (row) => objectOf(row).adoptingUsers,
  },
  {
    key: 'eligible', label: 'Elegíveis', type: 'int', infoKey: 'features.byCompany.eligible', sortable: true, startDescending: true,
    description: 'Usuários habilitados da empresa com papel que usa a funcionalidade, com a funcionalidade ligada.', value: (row) => objectOf(row).eligibleUsers,
  },
  {
    key: 'recurrence', label: 'Recorrentes', type: 'int', infoKey: 'features.byCompany.recurrent',
    description: 'Adotantes com uso em pelo menos 2 semanas diferentes nos 30 dias.', value: (row) => objectOf(row).recurrenceUsers,
  },
  {
    key: 'recurrenceShare', label: 'Recorrentes (% dos adotantes)', type: 'percent', infoKey: 'features.byCompany.recurrentShare', screen: false,
    value: recurrenceShareOf,
  },
  { key: 'events', label: 'Ações', type: 'int', infoKey: 'features.byCompany.events', description: 'Ações bem-sucedidas na funcionalidade nos 30 dias.' },
]);

export const companyAdoptionLines = (view) => {
  const source = objectOf(view);
  const lines = [];
  if (isNumber(source.minEligible)) lines.push('Mínimo de elegíveis por empresa: ' + formatInteger(source.minEligible));
  if (validJourney(source.journey)) lines.push('Jornada no fim da janela: ' + journeyLabel(validJourney(source.journey)));
  if (textOf(source.criterionText)) lines.push(textOf(source.criterionText));
  if (textOf(source.orderText)) lines.push('Ordem: ' + textOf(source.orderText));
  return lines;
};

export const COMPANY_ADOPTION_DISABLED_MESSAGE =
  'Empresas por adoção ainda não estão disponíveis neste ambiente (404): a Read API ainda não tem esta consulta.';

export const ROLE_TOTALS_TITLE = 'Com filtro de papel do usuário, recorrência e ações ficam vazias: elas só existem no total da empresa.';

export const COMBINED_CUTS_TEXT = 'Com mais de um corte (papel do usuário, porte e perfil da empresa), a lista vale, mas a ficha não tem adoção para a combinação.';

const CUT_FIELDS = ['roleClass', 'sizeBand', 'segment'];

const HIDDEN_NOTE_PREFIXES = [
  'Adoção em janela móvel de 30 dias',
  'Empresas por adoção:',
  'Ordem por ',
  'Fora da lista:',
  'A soma de adotantes e de elegíveis',
  'Com perfil, recorrência',
  'Com mais de um corte',
];

const BLOCKING_STATES = ['na', 'not_instrumented', 'disabled', 'no_eligible'];

const NOTICE_STATES = ['partial', 'insufficient'];

const KNOWN_STATES = ['ok'].concat(BLOCKING_STATES, NOTICE_STATES);

const ORDER_TEXTS = {
  'adoption:desc': 'Maior % de adoção primeiro; no empate, mais adotantes.',
  'adoption:asc': 'Menor % de adoção primeiro; no empate, menos adotantes.',
  'adopters:desc': 'Mais adotantes primeiro; no empate, maior % de adoção.',
  'adopters:asc': 'Menos adotantes primeiro; no empate, menor % de adoção.',
  'eligible:desc': 'Mais elegíveis primeiro; no empate, maior % de adoção.',
  'eligible:asc': 'Menos elegíveis primeiro; no empate, menor % de adoção.',
};

const ISO_DATE = /\b(\d{4})-(\d{2})-(\d{2})\b/g;

const textOf = (value) => (value === null || value === undefined ? '' : String(value).trim());

const firstOf = (value) => (Array.isArray(value) ? value[0] : value);

const numberOrNull = (value) => (isNumber(value) ? value : null);

const plural = (value, one, many) => formatInteger(value) + ' ' + (value === 1 ? one : many);

const positiveInteger = (value, fallback, max) => {
  const number = Math.floor(Number(value));
  if (!isFinite(number) || number < 1) return fallback;
  return max && number > max ? max : number;
};

const capitalized = (value) => (value ? value.charAt(0).toUpperCase() + value.substring(1) : '');

const sentence = (value) => {
  const clean = capitalized(textOf(value));
  if (!clean) return '';
  return /[.!?]$/.test(clean) ? clean : clean + '.';
};

export const brDates = (value) => textOf(value).replace(ISO_DATE, (match, year, month, day) => day + '/' + month + '/' + year);

export const validMinEligible = (value) => {
  const raw = textOf(value);
  if (!/^\d{1,5}$/.test(raw)) return DEFAULT_MIN_ELIGIBLE;
  const number = Number(raw);
  return number >= 1 && number <= MAX_MIN_ELIGIBLE ? number : DEFAULT_MIN_ELIGIBLE;
};

export const validCompanySort = (value) => (COMPANY_ADOPTION_SORTS.indexOf(textOf(value)) !== -1 ? textOf(value) : DEFAULT_COMPANY_ADOPTION_LIST.sort);

export const validJourney = (value) => {
  const raw = textOf(value);
  return JOURNEY_OPTIONS.some((option) => option.value === raw) ? raw : '';
};

export const validCompanyDesc = (value) => {
  const raw = textOf(firstOf(value)).toLowerCase();
  if (raw === '0' || raw === 'false') return false;
  if (raw === '1' || raw === 'true') return true;
  return DEFAULT_COMPANY_ADOPTION_LIST.desc;
};

export const companyAdoptionList = (list) => {
  const source = objectOf(list);
  return {
    minEligible: validMinEligible(source.minEligible),
    journey: validJourney(source.journey),
    sort: validCompanySort(source.sort),
    desc: source.desc === undefined ? DEFAULT_COMPANY_ADOPTION_LIST.desc : source.desc === true,
    page: positiveInteger(source.page, 1),
    pageSize: positiveInteger(source.pageSize, COMPANY_ADOPTION_PAGE_SIZE, MAX_PAGE_SIZE),
    base: textOf(source.base),
  };
};

export const companyAdoptionFromQuery = (query) => {
  const source = objectOf(query);
  const present = COMPANY_ADOPTION_QUERY_KEYS.some((key) => textOf(firstOf(source[key])) !== '');
  if (!present) return null;
  return companyAdoptionList({
    minEligible: textOf(firstOf(source[COMPANY_ADOPTION_QUERY.minEligible])),
    sort: textOf(firstOf(source[COMPANY_ADOPTION_QUERY.sort])),
    desc: validCompanyDesc(source[COMPANY_ADOPTION_QUERY.desc]),
    journey: textOf(firstOf(source[COMPANY_ADOPTION_QUERY.journey])),
  });
};

export const withoutCompanyAdoptionQuery = (query) => {
  const source = objectOf(query);
  const next = {};
  Object.keys(source).forEach((key) => {
    if (COMPANY_ADOPTION_QUERY_KEYS.indexOf(key) === -1) next[key] = source[key];
  });
  return next;
};

export const companyAdoptionRequest = (featureType, filters, list, now) => {
  const state = companyAdoptionList(list);
  const feature = textOf(featureType);
  const base = buildApiQuery(filters, { fields: COMPANY_ADOPTION_FIELDS, now: now });
  const baseKey = feature + '?' + toQueryString(base);
  const page = state.base === baseKey ? state.page : 1;
  const params = buildApiQuery(filters, {
    fields: COMPANY_ADOPTION_FIELDS,
    now: now,
    extra: { sort: state.sort, desc: state.desc, page: page, pageSize: state.pageSize, journey: state.journey },
  });
  params.minEligible = String(state.minEligible);
  return { baseKey: baseKey, params: params, key: feature + '?' + toQueryString(params), page: page };
};

export const minEligibleList = (list, value) =>
  Object.assign({}, companyAdoptionList(list), { minEligible: validMinEligible(value), page: 1 });

export const journeyList = (list, value) =>
  Object.assign({}, companyAdoptionList(list), { journey: validJourney(value), page: 1 });

export const sortedCompanyList = (list, column) => {
  const state = companyAdoptionList(list);
  const source = objectOf(column);
  const next = nextSort(state, textOf(source.sort) || textOf(source.key), source.startDescending);
  return Object.assign({}, state, { sort: validCompanySort(next.sort), desc: next.desc, page: 1 });
};

export const pagedCompanyList = (list, page, baseKey) =>
  Object.assign({}, companyAdoptionList(list), { page: positiveInteger(page, 1), base: textOf(baseKey) });

export const resizedCompanyList = (list, pageSize) =>
  Object.assign({}, companyAdoptionList(list), { pageSize: positiveInteger(pageSize, COMPANY_ADOPTION_PAGE_SIZE, MAX_PAGE_SIZE), page: 1 });

export const minEligibleOptions = (selected) => MIN_ELIGIBLE_OPTIONS.map((value) => ({
  value: value,
  label: String(value),
  on: value === selected,
}));

export const orderText = (sort, desc) => ORDER_TEXTS[validCompanySort(sort) + (desc === false ? ':asc' : ':desc')];

const factsText = (source) => [
  textOf(source.shardCode),
  textOf(source.sizeBand) ? optionLabel(SIZE_BAND_OPTIONS, textOf(source.sizeBand)) : '',
  profileText(textOf(source.segment)),
  journeyText(source.clientJourney === undefined ? source.journey : source.clientJourney),
].filter(Boolean).join(' · ');

export const companyAdoptionRow = (row, query, index) => {
  const source = objectOf(row);
  const companyId = textOf(source.companyId);
  const shardCode = textOf(source.shardCode);
  const adoption = metricCell(source.adoption, { ratio: false });
  const adopters = numberOrNull(source.adoptingUsers);
  const eligible = numberOrNull(source.eligibleUsers);
  const recurrence = numberOrNull(source.recurrenceUsers);
  const events = numberOrNull(source.events);
  const recurrenceShare = recurrence !== null && adopters ? shareOf(recurrence, adopters) : null;
  const rank = numberOrNull(source.rank);
  return {
    key: shardCode + ':' + (companyId || String(index || 0)),
    rank: rank,
    rankText: rank === null ? EMPTY : formatInteger(rank) + 'º',
    companyId: companyId,
    shardCode: shardCode,
    name: textOf(source.name) || companyId || 'Empresa',
    link: companyLink(companyId, shardCode, query),
    factsText: factsText(source),
    adoption: adoption,
    barPct: adoption.visible ? Math.max(0, Math.min(100, adoption.metric.value)) : null,
    usersText: ratioText(adopters, eligible),
    adoptersText: countText(adopters),
    eligibleText: countText(eligible),
    recurrenceText: countText(recurrence),
    recurrenceShareText: recurrenceShare === null ? '' : formatPercent(recurrenceShare) + ' dos adotantes',
    eventsText: countText(events),
  };
};

export const criterionText = (window, minEligible) => {
  const range = objectOf(window);
  const period = formatPeriod(range.from, range.to);
  const days = period === EMPTY ? 'nos 30 dias da adoção da ficha' : 'nos 30 dias de ' + period;
  return 'Adotantes ÷ elegíveis de cada empresa ' + days + ', com a mesma regra de elegibilidade da adoção da ficha. ' +
    'Só entram empresas com pelo menos ' + plural(validMinEligible(minEligible), 'elegível', 'elegíveis') + '.';
};

export const belowText = (summary, minEligible) => {
  const source = objectOf(summary);
  const below = numberOrNull(source.belowMinimum) || 0;
  const without = numberOrNull(source.withoutEligible) || 0;
  const companies = numberOrNull(source.companies);
  if (!below && !without) {
    return companies ? 'Nenhuma empresa ficou fora da lista pelo mínimo.' : '';
  }
  const parts = [];
  if (below) parts.push(plural(below, 'empresa', 'empresas') + ' com menos de ' + plural(validMinEligible(minEligible), 'elegível', 'elegíveis'));
  if (without) parts.push(plural(without, 'empresa', 'empresas') + ' sem elegível no recorte');
  return 'Fora da lista: ' + parts.join(' e ') + '; ' + (below + without === 1 ? 'ela conta' : 'elas contam') + ' nos totais.';
};

export const rankedText = (summary, total) => {
  const source = objectOf(summary);
  const ranked = numberOrNull(source.ranked);
  const companies = numberOrNull(source.companies);
  const count = ranked !== null ? ranked : numberOrNull(total);
  if (count === null || (count === 0 && !companies)) return '';
  const head = plural(count, 'empresa', 'empresas') + ' na lista';
  return companies !== null && companies !== count ? head + ', de ' + plural(companies, 'empresa', 'empresas') + ' com a funcionalidade no recorte' : head;
};

const pctText = (numerator, denominator) => {
  const value = shareOf(numerator, denominator);
  return formatInteger(numerator) + ' de ' + formatInteger(denominator) + (value === null ? '' : ' (' + formatPercent(value) + ')');
};

export const sumCheck = (summary, adoption) => {
  const source = objectOf(summary);
  const eligible = numberOrNull(source.eligibleUsers);
  const adopters = numberOrNull(source.adoptingUsers);
  if (!eligible || adopters === null) return { text: '', tone: 'muted', matches: null };
  const base = 'Soma de todas as empresas do recorte, inclusive as fora da lista: ' + pctText(adopters, eligible);
  const matches = source.matchesFeatureAdoption === true ? true : (source.matchesFeatureAdoption === false ? false : null);
  if (matches === true) return { text: base + ', igual à adoção da ficha.', tone: 'ok', matches: true };
  if (matches === false) {
    const detail = objectOf(adoption);
    const ficha = isNumber(detail.numerator) && isNumber(detail.denominator) ? '; a adoção da ficha é ' + pctText(detail.numerator, detail.denominator) : '';
    return { text: base + ficha + '.', tone: 'warn', matches: false };
  }
  return { text: base + '.', tone: 'muted', matches: null };
};

export const stateInfo = (data) => {
  const source = objectOf(data);
  const value = KNOWN_STATES.indexOf(textOf(source.state)) !== -1 ? textOf(source.state) : 'ok';
  const reason = brDates(source.stateReason);
  return { value: value, label: stateLabel(value), tone: stateTone(value), reason: reason };
};

const journeyEmpty = (data, journey) => stateInfo(data).value === 'disabled' && validJourney(journey) !== '' && !listOf(objectOf(data).rows).length;

export const blockedText = (data, journey) => {
  const source = objectOf(data);
  const state = stateInfo(source);
  if (BLOCKING_STATES.indexOf(state.value) === -1 || listOf(source.rows).length || journeyEmpty(source, journey)) return '';
  const base = sentence(state.reason) || stateDescription(state.value);
  const from = formatDateFull(source.availableFrom);
  const to = formatDateFull(source.availableTo);
  if (state.value !== 'na' || to === EMPTY) return base;
  if (from === to) return base + ' Há empresas por adoção calculadas só para ' + to + ': escolha um período que termine nesse dia.';
  return base + ' Escolha um período que termine em ' + to + ': os dias anteriores ainda podem estar sem cálculo.';
};

export const stateNotice = (data) => {
  const state = stateInfo(data);
  if (NOTICE_STATES.indexOf(state.value) === -1) return '';
  return state.label + (state.reason ? ': ' + state.reason : '') + '. Os percentuais da lista ainda podem mudar.';
};

export const emptyText = (data, minEligible, journey) => {
  const summary = objectOf(objectOf(data).summary);
  const companies = numberOrNull(summary.companies) || 0;
  const label = journeyLabel(validJourney(journey));
  if (!companies && label) {
    return 'Nenhuma empresa da jornada ' + label + ' com a funcionalidade neste recorte. Escolha Todas em Jornada no fim da janela para ver as outras empresas.';
  }
  if (!companies) return 'Nenhuma empresa com a funcionalidade neste recorte.';
  return 'Nenhuma empresa com pelo menos ' + plural(validMinEligible(minEligible), 'elegível', 'elegíveis') +
    ' neste recorte. Diminua o mínimo para ver as empresas menores.';
};

export const companyAdoptionPaging = (data, list) => {
  const source = objectOf(data);
  const state = companyAdoptionList(list);
  return {
    total: isNumber(source.total) && source.total >= 0 ? Math.floor(source.total) : listOf(source.rows).length,
    page: isNumber(source.page) && source.page >= 1 ? Math.floor(source.page) : state.page,
    pageSize: isNumber(source.pageSize) && source.pageSize >= 1 ? Math.floor(source.pageSize) : state.pageSize,
  };
};

export const companyAdoptionView = (data, options) => {
  const source = objectOf(data);
  const settings = objectOf(options);
  const list = companyAdoptionList(settings.list);
  const minEligible = isNumber(source.minEligible) ? validMinEligible(source.minEligible) : list.minEligible;
  const sort = textOf(source.sort) ? validCompanySort(source.sort) : list.sort;
  const desc = typeof source.desc === 'boolean' ? source.desc : list.desc;
  const rows = listOf(source.rows).map((row, index) => companyAdoptionRow(row, settings.query, index));
  const params = objectOf(settings.params);
  const roleCut = !!textOf(params.roleClass);
  const cuts = CUT_FIELDS.filter((key) => textOf(params[key]) !== '').length;
  const loadedJourney = settings.loadedJourney === undefined ? list.journey : validJourney(settings.loadedJourney);
  return {
    state: stateInfo(source),
    blockedText: blockedText(source, loadedJourney),
    notice: stateNotice(source),
    criterionText: criterionText(source.window, minEligible),
    orderText: orderText(sort, desc),
    rankedText: rankedText(source.summary, source.total),
    belowText: belowText(source.summary, minEligible),
    sum: sumCheck(source.summary, source.adoption),
    cutsText: cuts > 1 ? COMBINED_CUTS_TEXT : '',
    minEligible: minEligible,
    minOptions: minEligibleOptions(list.minEligible),
    journey: list.journey,
    sortState: { sort: list.sort, desc: list.desc },
    rows: rows,
    query: objectOf(settings.query),
    roleTitle: roleCut ? ROLE_TOTALS_TITLE : '',
    paging: companyAdoptionPaging(source, list),
    emptyText: emptyText(source, minEligible, loadedJourney),
  };
};

export const companyAdoptionEnvelope = (envelope) => {
  if (!envelope || typeof envelope !== 'object') return envelope;
  const notes = listOf(envelope.notes).filter((note) => !HIDDEN_NOTE_PREFIXES.some((prefix) => textOf(note).indexOf(prefix) === 0));
  return Object.assign({}, envelope, { notes: notes });
};

export const companyAdoptionError = (error) => {
  if (!error || error.kind !== 'disabled') return error;
  return Object.assign({}, error, { message: COMPANY_ADOPTION_DISABLED_MESSAGE });
};



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piFeatureCompaniesModel.js